import React, { FC, useCallback, useEffect, useState } from 'react'
import { z } from 'zod'
import { Header } from '../../components/header'
import { Steps } from '../../components/steps'

export const CollectionItem = z.object({
  id: z.number(),
  title: z.string(),
  image: z.string().nullable()
})
export type CollectionItem = z.infer<typeof CollectionItem>

export const Collection = z.array(CollectionItem)
export type Collection = z.infer<typeof Collection>

export const Input = z.object({
  header: z.string(),
  label: z.string(),
  label_wood: z.string(),
  aluminium_info: z.string(),
  steel_info: z.string(),
  unfinished_info: z.string(),
  finished_info: z.string(),
  metal: Collection,
  wood: Collection
})
export type Input = z.infer<typeof Input>

export type ApprovedData = {
  email: string
  material: string
  design: string
}

export type Props = {
  approvedData: ApprovedData
  goNextStep: (activeMetal: string, activeWood: string, finish: string) => void
  goBackStep: () => void
}

export const Step_2: FC<Props> = ({ approvedData, goNextStep, goBackStep }) => {
  const [input] = useState<Input | null>(() => {
    const inputContainer = document.getElementById('custom-sizes-step-2')
    const result = Input.safeParse(
      JSON.parse(inputContainer !== null ? inputContainer.innerText : '{}')
    )

    if (result.success) {
      return result.data
    }

    console.error(
      'Input data is invalid [dc919b5b0ff94b88909ffc3c5242cb2c]',
      result.error
    )
    return null
  })

  const [activeMaterial] = useState<string>(approvedData.material)
  const [activeCollection, setActiveCollection] = useState<Collection>([])
  const [activeMetal, setMetal] = useState<string>('')
  const [activeWood, setWood] = useState<string>('')
  const [activeFinish, setFinish] = useState<number>(-1)
  const [showButton, setShowButton] = useState<boolean>(false)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (input == null) {
      return
    }

    if (activeMaterial === 'Wood') {
      setActiveCollection(input.wood)
    } else if (activeMaterial === 'Metal') {
      setActiveCollection(input.metal)
    } else {
      setActiveCollection([])
    }
  }, [activeMaterial, input])

  useEffect(() => {
    if (activeMaterial === 'Wood') {
      if (activeFinish !== -1 && activeWood !== '') {
        setShowButton(true)
      }
    } else if (activeMaterial === 'Metal') {
      if (activeFinish !== -1 && activeMetal !== '') {
        setShowButton(true)
      }
    } else {
      setShowButton(false)
    }
  }, [activeMaterial, activeMetal, activeWood, activeFinish])

  const changeMetal = useCallback((metal: string) => {
    setMetal(metal)
  }, [])

  const changeWood = useCallback((wood: string) => {
    setWood(wood)
  }, [])

  const changeFinish = useCallback((e: number) => {
    setFinish(e)
  }, [])

  const goNext = useCallback(() => {
    const finish =
      activeCollection[activeFinish] !== null
        ? activeCollection[activeFinish].title
        : ''

    goNextStep(activeMetal, activeWood, finish)
  }, [activeCollection, activeFinish, activeMetal, activeWood, goNextStep])

  const goBack = useCallback(() => {
    goBackStep()
  }, [goBackStep])

  if (input == null) {
    return null
  }

  return (
    <div className="CS_Step2">
      <Steps step={2} allSteps={5} />
      {input.header && <Header text={input.header} />}

      {activeMaterial === 'Metal' && (
        <div className="CS_Step2-line">
          {input.label && (
            <label className="CS_Step2-label">{input.label}</label>
          )}

          <div className="CS_Step2-radioContainer">
            <div
              className={`CS_Step2-radio ${
                activeMetal === 'Steel' ? 'active' : ''
              }`}
              onClick={() => changeMetal('Steel')}
              tabIndex={0}
            >
              Steel
            </div>
            <div
              className={`CS_Step2-radio ${
                activeMetal === 'Aluminium' ? 'active' : ''
              }`}
              onClick={() => changeMetal('Aluminium')}
              tabIndex={0}
            >
              Aluminium
            </div>
          </div>

          <div className="CS_Step2-infoContainer">
            {input.aluminium_info && (
              <p>
                <b>Aluminium:</b> {input.aluminium_info}
              </p>
            )}
            {input.steel_info && (
              <p>
                <b>Steel:</b> {input.steel_info}
              </p>
            )}
          </div>
        </div>
      )}

      {activeMaterial === 'Wood' && (
        <div className="CS_Step2-line">
          {input.label_wood && (
            <label className="CS_Step2-label">{input.label_wood}</label>
          )}

          <div className="CS_Step2-radioContainer">
            <div
              className={`CS_Step2-radio ${
                activeWood === 'Unfinished' ? 'active' : ''
              }`}
              onClick={() => changeWood('Unfinished')}
              tabIndex={0}
            >
              Unfinished
            </div>
            <div
              className={`CS_Step2-radio ${
                activeWood === 'Finished' ? 'active' : ''
              }`}
              onClick={() => changeWood('Finished')}
              tabIndex={0}
            >
              Finished
            </div>
          </div>

          <div className="CS_Step2-infoContainer">
            {input.unfinished_info && (
              <p>
                <b>Unfinished:</b> {input.unfinished_info}
              </p>
            )}
            {input.finished_info && (
              <p>
                <b>Finished:</b> {input.finished_info}
              </p>
            )}
          </div>
        </div>
      )}

      <label className="CS_Step2-label">Select Finish Color:</label>

      <div className="CS_Step2-thumbnails">
        {activeCollection.length > 0 &&
          activeCollection.map((item, i) => (
            <div
              key={i}
              onClick={() => changeFinish(item.id)}
              className={`CS_Step2-thumbnail ${
                item.id === activeFinish ? 'active' : ''
              }`}
              tabIndex={0}
            >
              <div
                className="CS_Step2-image"
                style={{ backgroundImage: `url(${item?.image})` }}
              ></div>
              <div className="CS_Step2-title">{item?.title}</div>
            </div>
          ))}
      </div>

      <div className="CS_Step2-btnContainer">
        <button className="GoBack" onClick={goBack}>
          <svg
            width="16"
            height="10"
            viewBox="0 0 16 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.75 5L1.25 5M1.25 5L5.54032 0.999999M1.25 5L5.54032 9"
              stroke="#4E6280"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Back
        </button>
        {showButton === true && (
          <button
            className="o-button o-button--blue CS_Step2-btn"
            onClick={goNext}
          >
            Next
          </button>
        )}
      </div>
    </div>
  )
}
